import { Modal, Form, DatePicker, Button, Select, InputNumber, Checkbox, Input, Space } from 'antd'
import dayjs from 'dayjs'
import localeData from 'dayjs/plugin/localeData'
import weekday from 'dayjs/plugin/weekday'
import React, { useEffect, useMemo, useState } from 'react'

import { CreatorService } from '@services/CreatorService'
import { SchoolService } from '@services/SchoolService'
import { useConfirmation } from '@store/confirmation'
import { useNotifications } from '@store/notifications'

import { type Creator, type Product, type School, subscriptionsStatuses, subscriptionTerms } from '../../../../types'

dayjs.extend(weekday)
dayjs.extend(localeData)

interface AddSubscriptionModalProps {
    isOpen: boolean
    onClose: () => void
    school: School
    products: Product[]
    refetch: () => Promise<void>
}

const availableStatuses = subscriptionsStatuses.filter(s => s !== 'CANCELLED')

const AddSubscriptionModal: React.FC<AddSubscriptionModalProps> = ({ isOpen, onClose, school, products, refetch }) => {
    const [form] = Form.useForm()
    const [isLoading, setIsLoading] = useState(false)
    const [creators, setCreators] = useState<Creator[]>()
    const { showNotification } = useNotifications()
    const [selectedCreatorId, setSelectedCreatorId] = useState<string>()
    const confirm = useConfirmation()

    useEffect(() => {
        const fetchCreators = async () => {
            const creatorResults = await CreatorService.list()
            setCreators(creatorResults)

            // hardcode default to maths no problem for now
            const found = creatorResults.find(creator => creator.profileUrl === 'maths-no-problem')
            if (found) {
                setSelectedCreatorId(found.id)

                form.setFieldValue('creatorId', found.id)
            }
        }
        fetchCreators()
    }, [])

    useEffect(() => {
        form.setFieldValue('status', 'INACTIVE')
    }, [])

    const handleSubmit = async (values: any) => {
        let realStatus: (typeof availableStatuses)[number]
        let useRealStatus = false

        const now = new Date()
        const startDate = new Date(values.dateRange[0])
        const endDate = new Date(values.dateRange[1])

        if (startDate <= now && now <= endDate) {
            // subscription dates are in range
            realStatus = 'ACTIVE'
        } else if (now <= startDate) {
            // subscription date hasn't start yet
            realStatus = 'INACTIVE'
        } else if (now >= endDate) {
            realStatus = 'COMPLETED'
        } else {
            throw new Error('Status validation failed')
        }

        if (values.status !== realStatus) {
            const response = await confirm({
                title: 'The status does not match the start / end date',
                description: `Do you want to change it from ${values.status} to ${realStatus}. The subscription status checker will change this status when it is next triggered`,
                confirmText: 'Yes',
                cancelText: 'No'
            })

            if (response) {
                useRealStatus = true
            }
        }

        const formattedValues = {
            name: values.name,
            startDate: values.dateRange[0].toISOString(),
            endDate: values.dateRange[1].toISOString(),
            schoolId: school.id,
            productIds: values.productIds || [],
            status: useRealStatus ? realStatus : values.status,
            seatCount: values.seatCount,
            term: values.term,
            isInternal: values.isInternal,
            creatorId: values.creatorId
        }

        try {
            setIsLoading(true)
            await SchoolService.addSubscription(school.id, formattedValues)
            onClose()
            await refetch()
            form.resetFields()
        } catch (error) {
            showNotification({
                title: 'Error adding subscription',
                type: 'error'
            })
        } finally {
            setIsLoading(false)
        }
    }

    const filteredProducts = useMemo(() => {
        return products.filter(product => {
            if (selectedCreatorId) {
                return product.creatorId === selectedCreatorId
            }

            return false
        })
    }, [products, selectedCreatorId])

    const today = dayjs().set('hour', 0).set('minute', 0).set('second', 0).set('millisecond', 0)
    const oneYearFromToday = today.add(1, 'year')

    return (
        <Modal title="Add Subscription" open={isOpen} onCancel={onClose} footer={null}>
            <Form form={form} onFinish={handleSubmit} layout="vertical" initialValues={{ status: 'ACTIVE' }}>
                <Form.Item
                    name="name"
                    label="Name"
                    rules={[{ required: true, message: 'Please enter a subscription name' }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Please select a status' }]}>
                    <Select>
                        {Object.values(availableStatuses).map(status => (
                            <Select.Option key={status} value={status}>
                                {status}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="term"
                    label="Term"
                    rules={[{ required: true, message: 'Please select a term' }]}
                    initialValue="YEARLY"
                >
                    <Select>
                        {Object.values(subscriptionTerms).map(term => (
                            <Select.Option key={term} value={term}>
                                {term}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Internal Subscription (won't be billed)"
                    name="isInternal"
                    valuePropName="checked"
                    layout="horizontal"
                >
                    <Checkbox />
                </Form.Item>

                <Space size="large">
                    <Form.Item
                        name="dateRange"
                        label="Start & End Date"
                        rules={[{ required: true, message: 'Please select a start and end date' }]}
                        initialValue={[today, oneYearFromToday]}
                    >
                        <DatePicker.RangePicker />
                    </Form.Item>

                    {/* Add seat counts */}
                    <Form.Item name="seatCount" label="Seat Count">
                        <InputNumber min={1} />
                    </Form.Item>
                </Space>

                <Form.Item label="Creator" name="creatorId">
                    <Select
                        style={{ minWidth: '200px' }}
                        options={creators?.map(creator => ({
                            label: creator.name,
                            value: creator.id
                        }))}
                        onChange={creatorId => {
                            setSelectedCreatorId(creatorId)
                        }}
                    />
                </Form.Item>

                <Form.Item name="productIds" label="Products">
                    <Select
                        mode="multiple"
                        options={filteredProducts.map(product => ({ label: product.name, value: product.id }))}
                    />
                </Form.Item>

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={isLoading} disabled={isLoading}>
                        Add Subscription
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    )
}

export default AddSubscriptionModal
